import { timeStringToSecondsSinceMidnight } from '@/helpers/dateHelper';
import { hoursToSeconds } from 'date-fns';
import { Location, ReadEmployee } from '../gql/graphql.generated';
import { MonthlySortConfig } from '../sort/MonthlySortProvider';
import { strings } from '../translation/strings';

export interface PersonnelDetailsSettings {
  dateOfBirth: boolean;
  hiredDate: boolean;
  leftCompanyDate: boolean;
  endProbationDate: boolean;
}

export interface RosterValidationSettings {
  managementAttendance: boolean;
  mandatorySkills: boolean;
}

export interface SplitShiftsSettings {
  maxNumber: number;
  minSecondsBetween: number;
}

export enum OrganisationNameType {
  COMPANY = 'Company',
  DIVISION = 'Division',
  BRAND = 'Brand',
  CUSTOM = 'Custom'
}

export interface OrganisationNameSettings {
  type: OrganisationNameType;
  customName: string;
}

export interface AvailabilityDeadlineSettings {
  sendReminders: boolean;
  deadlineMonthDay: number;
  notifyMonthDay: number;
  notifyHour: number;
}

export interface ReassignmentSettings {
  model: 'on-request' | 'pull' | 'disabled';
  workAtAllLocations: boolean;
}

export interface BreakLengthSettings {
  minimumShiftLength: number;
  minimumBreakLength: number;
}

export interface BreakSettings {
  breakLengthLimits: BreakLengthSettings[];
  severeBreakWarnings: boolean;
  canSplitBreaks: boolean;
}

export type OptimiserEnabledSolvers = 'geneticAlgorithm' | 'cpSat' | 'both';

export interface Announcement {
  id: string;
  title: string;
  text: string;
  isActive: boolean;
  displayDate?: Date;
  showAtDate?: Date;
  // createdAt?: Date;
  // updatedAt?: Date;
}

export type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday' | 'holiday';

export interface DefaultShiftTime {
  start: number;
  end: number;
}

export type DefaultShiftTimes = Record<DayKey, DefaultShiftTime>;

export type DefaultShiftTimeString = { start: string; end: string };
export type DefaultShiftTimesString = Record<DayKey, DefaultShiftTimeString>;

export const defaultShiftTimes: DefaultShiftTimes = Object.fromEntries(
  strings.common.daysOfWeekFullStartMonday.map(day => [
    day.toLowerCase(),
    { start: timeStringToSecondsSinceMidnight('08:00'), end: timeStringToSecondsSinceMidnight('20:00') }
  ])
) as DefaultShiftTimes;

export interface SystemSettings {
  weekStartDay: number;
  futureAvailabilityMonths: number;
  includeBreaksInTotals: boolean;
  allowEssEditEmail: boolean;
  allowEssEditPhoneNumber: boolean;
  timeOffsetAfterLastBreak: number;
  timeOffsetBeforeFirstBreak: number;
  minuteIncrement: number;
  canDeleteRosters: boolean;
  displayEmployeeIds: boolean;
  editableDetails: PersonnelDetailsSettings;
  viewableDetails: PersonnelDetailsSettings;
  highlightSystemTabsColor: string;
  systemTabsTextColor: string;
  rosterValidation: RosterValidationSettings;
  optimiserDuration: number;
  optimiserManagementAttendance: boolean;
  optimiserEnabledSolvers: OptimiserEnabledSolvers;
  optimiserShowEvaluation: boolean;
  maxTime: number;
  minTime: number;
  maxCustomTemplates: number;
  organisationName: OrganisationNameSettings;
  splitShifts: SplitShiftsSettings;
  availabilityDeadlineSettings: AvailabilityDeadlineSettings;
  holidayRegion: string;
  minShiftDuration: number;
  maxShiftDuration: number;
  defaultShiftDuration: number;
  maxTimePeriods: number;
  advancedLeaveManagement: boolean;
  advancedContractsAndRoleManagement: boolean;
  reassignment: ReassignmentSettings;
  allocateStaffButton: boolean;
  historicalData: number;
  breakSettings: BreakSettings;
  defaultShiftTimes: DefaultShiftTimes;
}

const MIN_HOURS = 8;
const MAX_HOURS = 23;

export const defaultSettings: SystemSettings = {
  weekStartDay: 1,
  futureAvailabilityMonths: 6,
  includeBreaksInTotals: false,
  allowEssEditEmail: false,
  allowEssEditPhoneNumber: false,
  timeOffsetAfterLastBreak: 0,
  timeOffsetBeforeFirstBreak: 0,
  minuteIncrement: 15,
  canDeleteRosters: false,
  displayEmployeeIds: false,
  editableDetails: {
    dateOfBirth: true,
    hiredDate: true,
    leftCompanyDate: true,
    endProbationDate: true
  },
  viewableDetails: {
    dateOfBirth: true,
    hiredDate: true,
    leftCompanyDate: true,
    endProbationDate: true
  },
  highlightSystemTabsColor: '',
  systemTabsTextColor: '',
  rosterValidation: {
    managementAttendance: true,
    mandatorySkills: true
  },
  optimiserDuration: 30,
  optimiserManagementAttendance: true,
  optimiserEnabledSolvers: 'geneticAlgorithm',
  optimiserShowEvaluation: false,
  maxTime: hoursToSeconds(MAX_HOURS),
  minTime: hoursToSeconds(MIN_HOURS),
  maxCustomTemplates: 10,
  organisationName: {
    type: OrganisationNameType.COMPANY,
    customName: ''
  },
  splitShifts: {
    maxNumber: 1,
    minSecondsBetween: 0
  },
  availabilityDeadlineSettings: {
    sendReminders: false,
    deadlineMonthDay: 1,
    notifyMonthDay: 1,
    notifyHour: 8
  },
  holidayRegion: '',
  minShiftDuration: hoursToSeconds(2),
  maxShiftDuration: hoursToSeconds(12),
  defaultShiftDuration: hoursToSeconds(2),
  maxTimePeriods: 1,
  advancedLeaveManagement: false,
  advancedContractsAndRoleManagement: false,
  reassignment: {
    model: 'on-request',
    workAtAllLocations: false
  },
  allocateStaffButton: false,
  historicalData: 13,
  breakSettings: {
    breakLengthLimits: [{ minimumBreakLength: 0, minimumShiftLength: 0 }],
    severeBreakWarnings: false,
    canSplitBreaks: false
  },
  defaultShiftTimes: defaultShiftTimes
};

export interface StaffWithSettings {
  employee: ReadEmployee;
  settings: StaffSettings;
}

export interface StaffSettingsErrors {
  employeeId: string;
  errors: string[];
}

export interface StaffSettings {
  minMonthlyHours: number;
  maxMonthlyHours: number;
  minWeeklyHours: number;
  maxWeeklyHours: number;
  email: string;
  phoneNumber: string;
  workingPatterns: WorkingPatterns[];
  assignedSkills: string[];
  dateOfBirth?: Date;
  hiredDate?: Date;
  endProbationDate?: Date;
  leftCompanyDate?: Date;
  toBeDeleted?: boolean;
  hasBeenDeleted?: boolean;
  assignedPersonaIds: string[];
  assignedLocationIds: number[];
  notes?: string;
  preferences?: StaffPreferences;
}

export enum OpenClosingTimeOptions {
  hidden = 'HIDDEN',
  fieldsOnly = 'FIELDS',
  all = 'ALL'
}

export enum GanttGridLinesThickness {
  small = 'S',
  medium = 'M',
  large = 'L',
  extraLarge = 'XL'
}

export enum GanttGridLineSpacing {
  none = 0,
  tenMinutes = 10,
  fifteenMinutes = 15,
  thirtyMinutes = 30
}

export interface PersonalizationPreferences {
  ganttGridLines: GanttGridLineSpacing;
  ganttGridLinesThickness: GanttGridLinesThickness;
  openClosingTimes: OpenClosingTimeOptions;
}

export interface StaffPickerPrefernces {
  status: boolean;
  timeAvailable: boolean;
  allocation: boolean;
  role: boolean;
  holidays: boolean;
  attendances: boolean;
  workingHours: boolean;
  tasks: boolean;
  taskNames: boolean;
}

export interface StaffPreferences {
  staffPicker: StaffPickerPrefernces;
  taskDisplay: {
    [key: string]: boolean;
  };
  personalization: PersonalizationPreferences;
  monthlySortOrder: MonthlySortConfig[];
}

export const defaultStaffSettings: StaffSettings = {
  maxMonthlyHours: 0,
  minMonthlyHours: 0,
  minWeeklyHours: 0,
  maxWeeklyHours: 0,
  phoneNumber: '',
  email: '',
  workingPatterns: [
    { day: 0, slotNumber: 0, startTime: 0, endTime: 0, id: '' },
    { day: 1, slotNumber: 0, startTime: 0, endTime: 0, id: '' },
    { day: 2, slotNumber: 0, startTime: 0, endTime: 0, id: '' },
    { day: 3, slotNumber: 0, startTime: 0, endTime: 0, id: '' },
    { day: 4, slotNumber: 0, startTime: 0, endTime: 0, id: '' },
    { day: 5, slotNumber: 0, startTime: 0, endTime: 0, id: '' },
    { day: 6, slotNumber: 0, startTime: 0, endTime: 0, id: '' },
    { day: 7, slotNumber: 0, startTime: 0, endTime: 0, id: '' }
  ],
  assignedSkills: [],
  assignedPersonaIds: [],
  assignedLocationIds: []
};

export interface WorkingPatterns {
  startTime: number;
  endTime: number;
  day: number;
  slotNumber: number;
  id: string;
}

export interface LocationWithSettings {
  location: Location;
  settings: LocationSettings;
  details: LocationDetails;
}

export interface LocationDetails {
  holdingCompanyName: string;
  companyCode: string;
  companyName: string;
  divisionName: string;
  brandName: string;
  perfecture: string;
  openingDate?: Date;
  closedDate?: Date;
}

export const defaultLocationDetails: LocationDetails = {
  companyCode: '',
  companyName: '',
  divisionName: '',
  brandName: '',
  perfecture: '',
  holdingCompanyName: ''
};

export interface LocationSettings {
  supervisorId: string;
  announcements: Announcement[];
}

export const defaultLocationSettings: LocationSettings = {
  supervisorId: '',
  announcements: []
};
